import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '../stores/user-store'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: '/users',
        component: RouteViewComponent,
        children: [
          {
            name: 'users-list',
            path: '',
            component: () => import('../pages/users/UsersPage.vue'),
          },
          {
            name: 'users-admins',
            path: 'admins',
            component: () => import('../pages/users/AdminsPage.vue'),
          },
        ],
      },
      {
        name: 'games',
        path: 'games',
        component: () => import('../pages/games/GamesPage.vue'),
      },
      {
        name: 'tags',
        path: 'tags',
        component: () => import('../pages/tags/TagsPage.vue'),
      },
      {
        name: 'games-create',
        path: 'games/new',
        component: () => import('../pages/games/CreateGamePage.vue'),
      },
      {
        name: 'games-steam-import',
        path: 'games/steam-import',
        component: () => import('../pages/games/SteamImportPage.vue'),
      },
      {
        name: 'games-detail',
        path: 'games/:id',
        component: () => import('../pages/games/GameDetailPage.vue'),
      },
      {
        name: 'games-edit',
        path: 'games/:id/edit',
        component: () => import('../pages/games/CreateGamePage.vue'),
      },
      {
        name: 'games-edit-tags',
        path: 'games/:id/edit-tags',
        component: () => import('../pages/games/GameEditTagsPage.vue'),
      },
      {
        name: 'posts',
        path: 'posts',
        component: () => import('../pages/posts/PostsPage.vue'),
      },
      {
        name: 'finance',
        path: '/finance',
        component: RouteViewComponent,
        children: [
          {
            name: 'wallets',
            path: 'wallets',
            component: () => import('../pages/finance/WalletsPage.vue'),
          },
          {
            name: 'transactions',
            path: 'transactions',
            component: () => import('../pages/finance/TransactionsPage.vue'),
          },
          {
            name: 'tokens',
            path: 'tokens',
            component: () => import('../pages/finance/TokensPage.vue'),
          },
        ],
      },
      {
        name: 'promotions',
        path: 'promotions',
        component: () => import('../pages/promotions/PromotionsPage.vue'),
      },
      {
        name: 'audit-events',
        path: 'audit-events',
        component: () => import('../pages/audit-events/AuditEventsPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  const isAuthenticated = !!userStore.session

  if (
    to.matched.some(
      (record) => record.name === 'admin' || record.path.startsWith('/admin') || record.name === 'dashboard',
    )
  ) {
    if (!isAuthenticated) {
      next({ name: 'login' })
    } else {
      next()
    }
  } else if (to.matched.some((record) => record.path.startsWith('/auth'))) {
    if (isAuthenticated) {
      next({ name: 'dashboard' })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
